import React from 'react';
import { StyleSheet, View } from 'react-native';
import { SkeletonBone, SkeletonGroup } from '@/components/common/skeleton';
import { RADIUS } from '@/constants/shared/theme';

/** A `TodoListForm` text input: 10pt vertical padding + 15pt text + 1pt border. */
const INPUT_HEIGHT = 40;
const CADENCE_OPTION_COUNT = 3;
const TASK_ROW_COUNT = 3;
const ROLE_OPTION_WIDTHS = [96, 120] as const;
const PERSON_OPTION_WIDTHS = [104, 88, 112] as const;

/** A 14pt/700 field label sitting in its 17pt line box. */
const Label: React.FC<{ width: number }> = ({ width }) => (
  <View style={styles.labelRow}>
    <SkeletonBone width={width} height={14} />
  </View>
);

/**
 * Loading placeholder for the edit form, rendered inside the same chrome the
 * form uses (mobile scroll content / desktop GlassCard) so the page does not
 * jump when the list lands. Mirrors `TodoListForm` in edit mode top to bottom:
 * title input, the three cadence options, the Assign-to picker (hint + Role /
 * Person option rows), the two date inputs, the tasks editor (header + rows of
 * reorder arrows / input / Remove), the apply-now box, and the Cancel / Save
 * actions. Field gaps and heights are the form's own, so a skeleton field is as
 * tall as the real one.
 */
export const EditTodoListSkeleton: React.FC = () => (
  <SkeletonGroup style={styles.container}>
    {/* Title */}
    <View style={styles.field}>
      <Label width={36} />
      <SkeletonBone width="100%" height={INPUT_HEIGHT} borderRadius={RADIUS.sm} />
    </View>

    {/* Cadence */}
    <View style={styles.field}>
      <Label width={60} />
      <View style={styles.cadenceRow}>
        {Array.from({ length: CADENCE_OPTION_COUNT }).map((_, i) => (
          <View key={i} style={styles.cadenceOption}>
            <SkeletonBone width="100%" height={39} borderRadius={RADIUS.sm} />
          </View>
        ))}
      </View>
    </View>

    {/* Assign to */}
    <View style={styles.field}>
      <Label width={64} />
      <View style={styles.hintRow}>
        <SkeletonBone width="70%" height={12} />
      </View>
      <View style={styles.branchLabelRow}>
        <SkeletonBone width={32} height={11} />
      </View>
      <View style={styles.options}>
        {ROLE_OPTION_WIDTHS.map(width => (
          <SkeletonBone key={width} width={width} height={35} borderRadius={RADIUS.sm} />
        ))}
      </View>
      <View style={styles.branchLabelRow}>
        <SkeletonBone width={48} height={11} />
      </View>
      <View style={styles.options}>
        {PERSON_OPTION_WIDTHS.map(width => (
          <SkeletonBone key={width} width={width} height={35} borderRadius={RADIUS.sm} />
        ))}
      </View>
    </View>

    {/* Dates */}
    <View style={styles.datesRow}>
      <View style={styles.dateField}>
        <Label width={120} />
        <SkeletonBone width="100%" height={INPUT_HEIGHT} borderRadius={RADIUS.sm} />
      </View>
      <View style={styles.dateField}>
        <Label width={124} />
        <SkeletonBone width="100%" height={INPUT_HEIGHT} borderRadius={RADIUS.sm} />
      </View>
    </View>

    {/* Tasks */}
    <View style={styles.field}>
      <View style={styles.tasksHeader}>
        <Label width={44} />
        <SkeletonBone width={88} height={30} borderRadius={RADIUS.sm} />
      </View>
      {Array.from({ length: TASK_ROW_COUNT }).map((_, i) => (
        <View key={i} style={styles.taskRow}>
          <View style={styles.taskReorder}>
            <SkeletonBone width={20} height={22} borderRadius={4} />
            <SkeletonBone width={20} height={22} borderRadius={4} />
          </View>
          <View style={styles.taskInput}>
            <SkeletonBone width="100%" height={INPUT_HEIGHT} borderRadius={RADIUS.sm} />
          </View>
          <SkeletonBone width={64} height={28} borderRadius={RADIUS.sm} />
        </View>
      ))}
    </View>

    {/* Apply now */}
    <View style={styles.applyNowBox}>
      <SkeletonBone width={22} height={22} borderRadius={5} />
      <View style={styles.applyNowCopy}>
        <View style={styles.labelRow}>
          <SkeletonBone width="55%" height={14} />
        </View>
        <View style={styles.hintRow}>
          <SkeletonBone width="90%" height={12} />
        </View>
      </View>
    </View>

    {/* Actions */}
    <View style={styles.actions}>
      <SkeletonBone width={82} height={39} borderRadius={RADIUS.sm} />
      <SkeletonBone width={124} height={39} borderRadius={RADIUS.sm} />
    </View>
  </SkeletonGroup>
);

const styles = StyleSheet.create({
  // `TodoListForm` container / field / label geometry.
  container: { gap: 16 },
  field: { gap: 8 },
  labelRow: { minHeight: 17, justifyContent: 'center' },
  cadenceRow: { flexDirection: 'row', gap: 8 },
  cadenceOption: { flex: 1 },
  // `TodoTargetPicker`: 12pt hint, 11pt uppercase branch labels (4pt top
  // margin), wrapping option rows of 35pt pills.
  hintRow: { minHeight: 15, justifyContent: 'center' },
  branchLabelRow: { minHeight: 13, marginTop: 4, justifyContent: 'center' },
  options: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  datesRow: { flexDirection: 'row', gap: 12 },
  dateField: { flex: 1, gap: 8 },
  tasksHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 30,
  },
  // A task row is as tall as its two 22pt reorder arrows (46pt).
  taskRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  taskReorder: { gap: 2 },
  taskInput: { flex: 1 },
  // The apply-now box: 1pt border, 12pt padding, checkbox beside a title + a
  // one-line hint (the hint wraps to two lines only on narrow phones).
  applyNowBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    padding: 12,
    borderWidth: 1,
    borderColor: 'transparent',
    borderRadius: RADIUS.sm,
  },
  applyNowCopy: { flex: 1, gap: 2 },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 10,
    marginTop: 4,
  },
});
